/**
 * Audio notification utility for user attention alerts
 * Plays an audible "ding" sound for actions requiring immediate attention
 */

let audioContext = null;

/**
 * Initialize audio context (lazy initialization)
 */
const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

/**
 * Play a notification "ding" sound
 * @param {string} type - Type of notification: 'info', 'warning', 'error', 'success'
 */
export const playNotificationSound = (type = 'info') => {
  try {
    const ctx = getAudioContext();
    
    // Create oscillator for the ding sound
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    // Connect nodes
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    // Configure sound based on type
    const config = {
      info: { freq: 800, duration: 0.15 },
      warning: { freq: 600, duration: 0.2 },
      error: { freq: 400, duration: 0.25 },
      success: { freq: 1000, duration: 0.12 }
    };
    
    const { freq, duration } = config[type] || config.info;
    
    // Set frequency
    oscillator.frequency.setValueAtTime(freq, ctx.currentTime);
    
    // Create envelope for smooth attack and decay
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.01); // Quick attack
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration); // Decay
    
    // Play the sound
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration);
    
  } catch (error) {
    console.error('Failed to play notification sound:', error);
  }
};

/**
 * Play a double ding for high-priority alerts
 */
export const playUrgentNotification = () => {
  playNotificationSound('warning');
  setTimeout(() => playNotificationSound('warning'), 200);
};
